import React from 'react'

const PINE  = '#1a3d2e'
const SAGE  = '#4a7c59'
const INK   = '#111815'
const INK_LT = '#7a8c81'
const BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const EFFECTIVE_DATE = 'April 12, 2026'

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: PINE, marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #e8f5eb' }}>{title}</h2>
      {children}
    </div>
  )
}

function P({ children, style }) {
  return <p style={{ fontSize: 15, color: INK, lineHeight: 1.7, marginBottom: 12, ...style }}>{children}</p>
}

function Li({ children }) {
  return <li style={{ fontSize: 15, color: INK, lineHeight: 1.7, marginBottom: 6 }}>{children}</li>
}

export default function PrivacyPolicy() {
  return (
    <div style={{ fontFamily: BODY, background: '#faf9f6', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: PINE, padding: '20px 0' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="/" style={{ fontSize: 18, fontWeight: 700, color: 'white', textDecoration: 'none', letterSpacing: '-0.02em' }}>ClearPath PT</a>
          <a href="/" style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>← Back to Home</a>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 80px' }}>
        <div style={{ marginBottom: 40 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: SAGE, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Legal</p>
          <h1 style={{ fontSize: 36, fontWeight: 700, color: INK, marginBottom: 8, letterSpacing: '-0.02em' }}>Privacy Policy</h1>
          <p style={{ fontSize: 14, color: INK_LT }}>Effective Date: {EFFECTIVE_DATE} · ClearPath PT, LLC</p>
          <div style={{ marginTop: 16, padding: '12px 16px', background: '#e8f5eb', borderRadius: 10, fontSize: 14, color: SAGE, borderLeft: '4px solid ' + SAGE }}>
            This policy also serves as our <strong>HIPAA Notice of Privacy Practices</strong> for the Services we provide as a Business Associate.
          </div>
        </div>

        <P>This Privacy Policy describes how ClearPath PT, LLC ("ClearPath PT", "we", "us", or "our") collects, uses, discloses, and protects information about clinics, staff, and patients who interact with our Service. By using our Service, you agree to the practices described in this Policy.</P>

        <Section title="1. Information We Collect">
          <h3 style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 8 }}>Clinic and Staff Information</h3>
          <P>When a clinic registers or staff members use the Service, we collect clinic name, address, phone, email, staff usernames and roles, usage data, and IP/device information for security purposes. Billing information is processed by Stripe — we never store full card numbers.</P>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 8, marginTop: 16 }}>Patient Information (Protected Health Information)</h3>
          <P>As a HIPAA Business Associate, we process patient PHI on behalf of clinics. This may include patient demographics, insurance information, medical history, treatment notes, session documentation, exercise records, and intake form responses.</P>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 8, marginTop: 16 }}>Automatically Collected Data</h3>
          <P>We automatically collect audit logs of all PHI access and modifications for HIPAA compliance. This data is retained for a minimum of 6 years per HIPAA requirements.</P>
        </Section>

        <Section title="2. How We Use Information">
          <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
            <Li>To operate, maintain, and improve the ClearPath PT platform</Li>
            <Li>To process transactions and send administrative communications</Li>
            <Li>To process PHI only as directed by and on behalf of the clinic that submitted it</Li>
            <Li>To detect and prevent fraud, security incidents, and illegal activity</Li>
            <Li>To comply with HIPAA audit and record-keeping requirements</Li>
          </ul>
          <P>We do not sell PHI or use PHI for marketing purposes. AI features process session context only to generate educational responses — we do not use identifiable PHI to train AI models.</P>
        </Section>

        <Section title="3. How We Share Information — Sub-Processor Disclosure">
          <P>We do not sell your personal information. We may share information only with the following categories of recipients:</P>
          <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
            <Li><strong>Infrastructure &amp; Hosting:</strong> Amazon Web Services, Inc. (AWS) — HIPAA-eligible cloud infrastructure with executed Business Associate Agreement. AWS services used include compute, storage, and managed database services.</Li>
            <Li><strong>Payments:</strong> Stripe, Inc. (payment processing) — PCI-DSS Level 1 certified; no PHI is transmitted to Stripe.</Li>
            <Li><strong>Email:</strong> SendGrid / Twilio (transactional email) — used for appointment reminders and system notifications; messages contain only the minimum necessary information.</Li>
            <Li><strong>AI Services:</strong> Third-party AI model providers used to power the Caleb assistant feature — PHI submitted to AI features is subject to our data minimization practices and applicable BAAs or equivalent data processing agreements.</Li>
            <Li><strong>Legal Requirements:</strong> When required by law, court order, or to protect rights, property, or safety.</Li>
            <Li><strong>Business Transfers:</strong> In connection with a merger or acquisition, subject to confidentiality protections and successor BAA obligations.</Li>
          </ul>
          <P>We maintain executed Business Associate Agreements with all sub-processors that handle PHI, as required by 45 CFR §164.504(e).</P>
        </Section>

        <Section title="4. Data Security">
          <P>We implement the following technical and organizational safeguards to protect your information:</P>
          <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
            <Li>Encryption in transit (TLS 1.2+) and at rest (AES-256)</Li>
            <Li>HIPAA-compliant AWS infrastructure with executed AWS Business Associate Agreement</Li>
            <Li>Role-based access controls — users can only access their clinic's data</Li>
            <Li>Comprehensive audit logging of all PHI access and modifications</Li>
            <Li>Regular security risk assessments per HIPAA Security Rule (45 CFR §164.308(a)(1))</Li>
            <Li>Automated database backups with 30-day retention</Li>
            <Li>Unique user identification and automatic session timeouts per HIPAA Technical Safeguards (45 CFR §164.312)</Li>
          </ul>
        </Section>

        <Section title="5. Breach Notification">
          <P>In the event of a breach affecting unsecured PHI, we will notify affected covered entity clinics without unreasonable delay and within no more than 60 calendar days of discovery, as required by the HIPAA Breach Notification Rule (45 CFR §164.400–414). Notification will include, to the extent reasonably possible:</P>
          <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
            <Li>A description of what happened, including the date of the breach and date of discovery</Li>
            <Li>The types of PHI involved</Li>
            <Li>Steps individuals should take to protect themselves</Li>
            <Li>What we are doing to investigate, mitigate, and prevent future breaches</Li>
            <Li>Contact information for questions</Li>
          </ul>
          <P>We will assist covered entity clinics in meeting their own Breach Notification obligations to patients and the U.S. Department of Health and Human Services (HHS), as required by our Business Associate Agreement.</P>
        </Section>

        <Section title="6. Data Retention">
          <P>We retain data according to the following schedule:</P>
          <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
            <Li><strong>Clinic and staff account data:</strong> Retained for the duration of active subscription plus 90 days after termination to allow for data export.</Li>
            <Li><strong>Patient PHI:</strong> Retained as directed by the clinic in accordance with applicable state and federal medical record retention requirements. Clinics remain the custodian of their PHI.</Li>
            <Li><strong>HIPAA audit logs:</strong> Retained for a minimum of 6 years from the date of creation or last effective date, per 45 CFR §164.530(j).</Li>
            <Li><strong>Billing records:</strong> Retained for 7 years for tax and financial compliance purposes.</Li>
            <Li><strong>Backups:</strong> Automated database backups retained for 30 days.</Li>
          </ul>
          <P>Upon account termination, clinics may request a full data export within 30 days. Following the export window, data will be securely deleted or de-identified.</P>
        </Section>

        <Section title="7. Your Rights">
          <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
            <Li><strong>Access and Correction:</strong> Clinic administrators can access and update clinic information in account settings. Patients should contact their clinic directly for PHI access requests under HIPAA's individual rights provisions (45 CFR §164.524).</Li>
            <Li><strong>Data Portability:</strong> Clinics may export their data at any time through the Service export feature or by contacting us at legal@clearpath-pt.com.</Li>
            <Li><strong>California Residents (CCPA/CPRA):</strong> Right to know, right to deletion, and right to opt out of sale of personal information. We do not sell personal information. To exercise these rights, contact legal@clearpath-pt.com.</Li>
            <Li><strong>Washington Residents:</strong> Residents of Washington State may have additional rights regarding their health data under the My Health My Data Act. Contact us to exercise any applicable rights.</Li>
          </ul>
        </Section>

        <Section title="8. Children's Privacy">
          <P>The Service is not directed at children under 13. We do not knowingly collect personal information from children under 13 without verifiable parental consent. If you believe we have inadvertently collected such information, please contact us immediately at legal@clearpath-pt.com.</P>
        </Section>

        <Section title="9. Changes to This Policy">
          <P>We may update this Privacy Policy periodically. We will notify users via email at the address on file for material changes at least 30 days before they take effect. The updated policy will also be posted at clearpath-pt.com/privacy with a revised effective date. Continued use of the Service after the effective date of any changes constitutes acceptance of those changes.</P>
        </Section>

        <Section title="10. Contact Us">
          <P>For privacy-related questions, data requests, or to report a security concern:</P>
          <div style={{ background: 'white', border: '1.5px solid #dde8e0', borderRadius: 12, padding: '16px 20px', marginTop: 8 }}>
            <p style={{ fontSize: 15, color: INK, margin: 0 }}><strong>ClearPath PT, LLC</strong></p>
            <p style={{ fontSize: 15, color: INK, margin: '4px 0 0' }}>Alabama, United States</p>
            <p style={{ fontSize: 15, color: SAGE, margin: '4px 0 0' }}>legal@clearpath-pt.com</p>
          </div>
        </Section>

        {/* Footer links */}
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid #dde8e0', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <a href="/terms" style={{ fontSize: 14, color: SAGE, textDecoration: 'none', fontWeight: 600 }}>Terms of Service →</a>
          <a href="/baa" style={{ fontSize: 14, color: SAGE, textDecoration: 'none', fontWeight: 600 }}>Business Associate Agreement →</a>
          <a href="/" style={{ fontSize: 14, color: INK_LT, textDecoration: 'none' }}>← Back to ClearPath PT</a>
        </div>
      </div>
    </div>
  )
}
